import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import ImageHistory, { saveToHistory } from './ImageHistory';
import BatchGeneration from './BatchGeneration';
import ImageToVideo from './ImageToVideo';
import { generateImageWithGemini } from '../../../utils/gemini';

type TabType = 'single' | 'batch' | 'i2v' | 'history';

export default function GeneratorInterface() {
  const { t } = useTranslation();
  const [prompt, setPrompt] = useState('');
  const [mode, setMode] = useState<'text-to-image' | 'image-to-image'>('text-to-image');
  const [aspectRatio, setAspectRatio] = useState('1:1');
  const [style, setStyle] = useState('none');
  const [prefilled, setPrefilled] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedImage, setGeneratedImage] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('single');
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);
  const [generateError, setGenerateError] = useState<string | null>(null);
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const downloadMenuRef = useRef<HTMLDivElement>(null);

  // 图生图相关状态
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const [uploadedFileName, setUploadedFileName] = useState('');
  const [isDraggingUpload, setIsDraggingUpload] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const promptRef = useRef<HTMLTextAreaElement>(null);

  // 点击外部关闭下载菜单
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (downloadMenuRef.current && !downloadMenuRef.current.contains(e.target as Node)) {
        setShowDownloadMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // 从画廊自动填充提示词
  useEffect(() => {
    const saved = localStorage.getItem('ai_prefill_prompt');
    if (saved) {
      setPrompt(saved);
      localStorage.removeItem('ai_prefill_prompt');
      setPrefilled(true);
      setActiveTab('single');
      setTimeout(() => {
        promptRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        promptRef.current?.focus();
      }, 300);
      setTimeout(() => setPrefilled(false), 3000);
    }
  }, []);

  const handleUsePromptFromHistory = (p: string) => {
    setPrompt(p);
    setActiveTab('single');
    setTimeout(() => {
      promptRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      promptRef.current?.focus();
    }, 200);
  };

  // 处理图片上传
  const handleFileSelect = useCallback((file: File) => {
    if (!file.type.startsWith('image/')) {
      setGenerateError('请上传图片文件（JPG、PNG、WebP 等）');
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      setGenerateError('图片大小不能超过 20MB');
      return;
    }
    setGenerateError(null);
    setUploadedFileName(file.name);
    const reader = new FileReader();
    reader.onload = (e) => {
      setUploadedImage(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  }, []);

  const handleUploadDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDraggingUpload(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  }, [handleFileSelect]);

  const styles = [
    { id: 'none', nameKey: 'gen.style.none', icon: 'ri-image-line', color: 'bg-gray-700 text-gray-400' },
    { id: 'realistic', nameKey: 'gen.style.realistic', icon: 'ri-camera-line', color: 'bg-amber-900/40 text-amber-400' },
    { id: 'anime', nameKey: 'gen.style.anime', icon: 'ri-star-line', color: 'bg-pink-900/40 text-pink-400' },
    { id: 'artistic', nameKey: 'gen.style.artistic', icon: 'ri-brush-line', color: 'bg-orange-900/40 text-orange-400' },
    { id: 'digital', nameKey: 'gen.style.digital', icon: 'ri-computer-line', color: 'bg-emerald-900/40 text-emerald-400' },
  ];

  const handleGenerate = async () => {
    if (!prompt.trim()) return;
    setGenerateError(null);

    // 图生图模式暂不支持（Gemini API 需要特殊处理）
    if (mode === 'image-to-image') {
      setGenerateError('图生图功能即将推出，敬请期待');
      return;
    }

    setIsGenerating(true);
    setGeneratedImage(null);
    setShowSuccess(false);

    try {
      // 使用 Gemini API 生成图片
      const imageUrl = await generateImageWithGemini(prompt, style, aspectRatio);

      setGeneratedImage(imageUrl);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);

      const ts = Date.now();
      saveToHistory({ id: `single_${ts}`, imageUrl, prompt, style, aspectRatio, createdAt: ts });
      setHistoryRefreshKey((k) => k + 1);
    } catch (error: any) {
      console.error('Generation failed:', error);
      setGenerateError(error?.message || '生成失败，请检查网络后重试');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownload = async (targetWidth?: number, targetHeight?: number, label?: string) => {
    if (!generatedImage) return;
    setShowDownloadMenu(false);

    try {
      if (targetWidth && targetHeight) {
        // 高清/4K：用 canvas 放大后下载
        const img = new window.Image();
        img.crossOrigin = 'anonymous';
        await new Promise<void>((resolve, reject) => {
          img.onload = () => resolve();
          img.onerror = () => reject();
          img.src = generatedImage;
        });
        const canvas = document.createElement('canvas');
        canvas.width = targetWidth;
        canvas.height = targetHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('canvas error');
        ctx.drawImage(img, 0, 0, targetWidth, targetHeight);
        canvas.toBlob((blob) => {
          if (!blob) return;
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `ai-generated-${label}-${Date.now()}.jpg`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        }, 'image/jpeg', 0.95);
        return;
      }

      // 标准：直接下载原图
      const response = await fetch(generatedImage);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `ai-generated-${Date.now()}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch {
      const link = document.createElement('a');
      link.href = generatedImage;
      link.download = `ai-generated-${Date.now()}.jpg`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const tabs: { id: TabType; labelKey: string; icon: string }[] = [
    { id: 'single', labelKey: 'gen.tabSingle', icon: 'ri-image-add-line' },
    { id: 'batch', labelKey: 'gen.tabBatch', icon: 'ri-stack-line' },
    { id: 'i2v', labelKey: 'gen.tabI2V', icon: 'ri-video-line' },
    { id: 'history', labelKey: 'gen.tabHistory', icon: 'ri-history-line' },
  ];

  return (
    <section className="py-16 min-h-screen bg-[#1a1b1e]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h1 className="text-5xl lg:text-6xl font-display font-black text-white mb-5">
            {t('gen.title')}
          </h1>
          <p className="text-xl text-gray-400 font-semibold">{t('gen.subtitle')}</p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* 左侧控制面板 */}
          <div className="lg:col-span-1 space-y-6">
            {/* 模式选择 */}
            <div className="bg-[#252830] rounded-2xl p-6 shadow-lg border border-gray-800">
              <h3 className="font-black text-white mb-4 text-sm uppercase tracking-wider">{t('gen.mode')}</h3>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => { setMode('text-to-image'); setUploadedImage(null); }}
                  className={`px-4 py-4 rounded-xl text-sm font-bold transition-all cursor-pointer whitespace-nowrap ${
                    mode === 'text-to-image' ? 'bg-primary text-white shadow-lg' : 'bg-[#1e2025] text-gray-400 hover:bg-[#2d3139] border border-gray-700'
                  }`}
                >
                  <i className="ri-text text-xl block mb-1.5"></i>
                  {t('gen.textToImage')}
                </button>
                <button
                  onClick={() => setMode('image-to-image')}
                  className={`px-4 py-4 rounded-xl text-sm font-bold transition-all cursor-pointer whitespace-nowrap ${
                    mode === 'image-to-image' ? 'bg-primary text-white shadow-lg' : 'bg-[#1e2025] text-gray-400 hover:bg-[#2d3139] border border-gray-700'
                  }`}
                >
                  <i className="ri-image-line text-xl block mb-1.5"></i>
                  {t('gen.imageToImage')}
                </button>
              </div>
            </div>

            {/* 比例选择 */}
            <div className="bg-[#252830] rounded-2xl p-6 shadow-lg border border-gray-800">
              <h3 className="font-black text-white mb-4 text-sm uppercase tracking-wider">{t('gen.aspectRatio')}</h3>
              <div className="grid grid-cols-3 gap-2">
                {['1:1', '16:9', '9:16'].map((ratio) => (
                  <button
                    key={ratio}
                    onClick={() => setAspectRatio(ratio)}
                    className={`px-3 py-3 rounded-xl text-sm font-bold transition-all cursor-pointer whitespace-nowrap ${
                      aspectRatio === ratio ? 'bg-primary text-white' : 'bg-[#1e2025] text-gray-400 hover:bg-[#2d3139] border border-gray-700'
                    }`}
                  >
                    {ratio}
                  </button>
                ))}
              </div>
            </div>

            {/* 风格选择 */}
            <div className="bg-[#252830] rounded-2xl p-6 shadow-lg border border-gray-800">
              <h3 className="font-black text-white mb-4 text-sm uppercase tracking-wider">{t('gen.style')}</h3>
              <div className="space-y-2">
                {styles.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => setStyle(s.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold transition-all cursor-pointer whitespace-nowrap ${
                      style === s.id ? 'bg-primary/20 text-primary border border-primary' : 'bg-[#1e2025] text-gray-400 hover:bg-[#2d3139] border border-gray-700'
                    }`}
                  >
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${s.color}`}>
                      <i className={`${s.icon} text-base`}></i>
                    </div>
                    <span>{t(s.nameKey)}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* 右侧主区域 */}
          <div className="lg:col-span-2 space-y-6">
            {/* Tab 切换 */}
            <div className="bg-[#252830] rounded-2xl p-1.5 shadow-lg border border-gray-800 flex gap-1">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 flex items-center justify-center gap-2 px-3 py-3 rounded-xl text-sm font-bold transition-all cursor-pointer whitespace-nowrap ${
                    activeTab === tab.id ? 'bg-primary text-white shadow-md' : 'text-gray-500 hover:bg-[#2d3139] hover:text-gray-300'
                  }`}
                >
                  <i className={`${tab.icon} text-base`}></i>
                  {t(tab.labelKey)}
                </button>
              ))}
            </div>

            {/* 单图生成 Tab */}
            {activeTab === 'single' && (
              <div className="bg-[#252830] rounded-2xl p-8 shadow-lg border border-gray-800">
                {showSuccess && (
                  <div className="mb-5 flex items-center gap-3 px-4 py-3 bg-green-500/10 border border-green-500/30 rounded-xl">
                    <i className="ri-check-circle-fill text-green-400 text-xl"></i>
                    <p className="text-green-300 text-sm font-bold">✨ {t('gen.successMsg')}</p>
                  </div>
                )}
                {prefilled && (
                  <div className="mb-5 flex items-center gap-3 px-4 py-3 bg-green-500/10 border border-green-500/30 rounded-xl animate-pulse">
                    <i className="ri-check-circle-fill text-green-400 text-xl"></i>
                    <p className="text-green-300 text-sm font-bold">{t('gen.promptPrefilled')}</p>
                  </div>
                )}
                {generateError && (
                  <div className="mb-5 flex items-center gap-3 px-4 py-3 bg-red-500/10 border border-red-500/30 rounded-xl">
                    <i className="ri-error-warning-line text-red-400 text-xl"></i>
                    <p className="text-red-300 text-sm font-bold">{generateError}</p>
                  </div>
                )}

                {/* 提示词输入 */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <label className="block text-sm font-black text-white">{t('gen.promptLabel')}</label>
                    {prompt && (
                      <button onClick={() => setPrompt('')} className="text-xs text-gray-500 hover:text-gray-300 font-semibold flex items-center gap-1 cursor-pointer whitespace-nowrap">
                        <i className="ri-close-circle-line"></i>
                        {t('gen.clearPrompt')}
                      </button>
                    )}
                  </div>
                  <div className={`relative rounded-xl transition-all duration-300 ${prefilled ? 'ring-2 ring-green-400 ring-offset-1 ring-offset-[#252830]' : ''}`}>
                    <textarea
                      ref={promptRef}
                      value={prompt}
                      onChange={(e) => setPrompt(e.target.value)}
                      placeholder={t('gen.promptPlaceholder')}
                      maxLength={500}
                      disabled={isGenerating}
                      className="w-full h-36 px-4 py-3 border border-gray-700 bg-[#1e2025] text-gray-200 placeholder-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary resize-none text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    />
                    <span className="absolute bottom-3 right-3 text-xs text-gray-600 font-medium">{prompt.length}/500</span>
                  </div>
                  <p className="mt-2 text-xs text-gray-500 font-medium">{t('gen.promptTip')}</p>
                </div>

                {/* 图生图上传区域 */}
                {mode === 'image-to-image' && (
                  <div className="mb-6">
                    <label className="block text-sm font-black text-white mb-3">{t('gen.uploadLabel')}</label>
                    <div className="border-2 border-dashed border-gray-700 rounded-xl p-8 text-center bg-[#1e2025]">
                      <div className="w-14 h-14 flex items-center justify-center mx-auto mb-3 bg-gray-700 rounded-2xl">
                        <i className="ri-time-line text-3xl text-gray-500"></i>
                      </div>
                      <p className="text-sm text-gray-400 font-bold mb-1">图生图功能即将推出</p>
                      <p className="text-xs text-gray-600 font-medium">敬请期待</p>
                    </div>
                  </div>
                )}

                {/* 生成按钮 */}
                <button
                  onClick={handleGenerate}
                  disabled={!prompt.trim() || isGenerating || mode === 'image-to-image'}
                  className={`w-full px-8 py-4 rounded-xl font-black text-lg transition-all flex items-center justify-center gap-3 whitespace-nowrap ${
                    prompt.trim() && !isGenerating && mode !== 'image-to-image'
                      ? 'bg-primary text-white hover:bg-primary/90 hover:shadow-2xl hover:shadow-primary/40 cursor-pointer'
                      : 'bg-gray-700 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  {isGenerating ? (
                    <>
                      <i className="ri-loader-4-line text-xl animate-spin"></i>
                      {t('gen.generating')}
                      <span className="text-sm font-medium opacity-70">（最长约 30 秒）</span>
                    </>
                  ) : (
                    <>
                      <i className="ri-magic-line text-xl"></i>
                      {t('gen.generateBtn')}
                      <span className="ml-1 px-2.5 py-0.5 bg-accent text-white text-xs rounded-full font-black">{t('gen.free')}</span>
                    </>
                  )}
                </button>

                {/* 结果区域 */}
                <div className="mt-8">
                  {generatedImage ? (
                    <div className="space-y-4">
                      <div className="relative rounded-xl overflow-hidden border border-gray-700 bg-[#1e2025]">
                        <img
                          src={generatedImage}
                          alt="Generated"
                          className="w-full object-contain"
                          style={{ minHeight: '480px', maxHeight: '700px' }}
                        />
                      </div>
                      <div className="flex gap-3">
                        {/* 下载按钮（带下拉菜单） */}
                        <div className="relative flex-1" ref={downloadMenuRef}>
                          <button
                            onClick={() => setShowDownloadMenu((v) => !v)}
                            className="w-full px-5 py-3.5 bg-primary text-white rounded-xl font-bold text-sm hover:bg-primary/90 transition-all flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
                          >
                            <i className="ri-download-line text-lg"></i>
                            {t('gen.download')}
                            <i className={`ri-arrow-${showDownloadMenu ? 'up' : 'down'}-s-line text-base ml-1`}></i>
                          </button>

                          {showDownloadMenu && (
                            <div className="absolute bottom-full left-0 mb-2 w-full bg-[#2d3139] border border-gray-700 rounded-xl shadow-2xl overflow-hidden z-50">
                              <button
                                onClick={() => handleDownload()}
                                className="w-full flex items-center gap-3 px-4 py-3.5 hover:bg-[#3a3f4a] transition-colors cursor-pointer text-left"
                              >
                                <div className="w-8 h-8 flex items-center justify-center bg-gray-700 rounded-lg flex-shrink-0">
                                  <i className="ri-image-line text-gray-300 text-sm"></i>
                                </div>
                                <div>
                                  <p className="text-white text-sm font-bold">标准画质</p>
                                  <p className="text-gray-500 text-xs">原始尺寸 · 快速下载</p>
                                </div>
                              </button>
                              <div className="h-px bg-gray-700/60 mx-3"></div>
                              <button
                                onClick={() => {
                                  const dims = getDlDimensions(aspectRatio, 'hd');
                                  handleDownload(dims.w, dims.h, 'HD');
                                }}
                                className="w-full flex items-center gap-3 px-4 py-3.5 hover:bg-[#3a3f4a] transition-colors cursor-pointer text-left"
                              >
                                <div className="w-8 h-8 flex items-center justify-center bg-amber-900/50 rounded-lg flex-shrink-0">
                                  <i className="ri-hd-line text-amber-400 text-sm"></i>
                                </div>
                                <div>
                                  <p className="text-white text-sm font-bold">高清 HD</p>
                                  <p className="text-gray-500 text-xs">1920×1080 · 高品质</p>
                                </div>
                              </button>
                              <div className="h-px bg-gray-700/60 mx-3"></div>
                              <button
                                onClick={() => {
                                  const dims = getDlDimensions(aspectRatio, '4k');
                                  handleDownload(dims.w, dims.h, '4K');
                                }}
                                className="w-full flex items-center gap-3 px-4 py-3.5 hover:bg-[#3a3f4a] transition-colors cursor-pointer text-left"
                              >
                                <div className="w-8 h-8 flex items-center justify-center bg-emerald-900/50 rounded-lg flex-shrink-0">
                                  <i className="ri-4k-line text-emerald-400 text-sm"></i>
                                </div>
                                <div>
                                  <p className="text-white text-sm font-bold">超清 4K</p>
                                  <p className="text-gray-500 text-xs">3840×2160 · 最高画质</p>
                                </div>
                              </button>
                            </div>
                          )}
                        </div>

                        <button
                          onClick={() => setGeneratedImage(null)}
                          className="px-5 py-3.5 bg-[#1e2025] text-gray-300 rounded-xl font-bold text-sm hover:bg-[#2d3139] transition-all flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap border border-gray-700"
                        >
                          <i className="ri-refresh-line text-lg"></i>
                          {t('gen.new')}
                        </button>
                        <button
                          onClick={() => setActiveTab('history')}
                          className="px-5 py-3.5 bg-[#1e2025] text-gray-300 rounded-xl font-bold text-sm hover:bg-[#2d3139] transition-all flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap border border-gray-700"
                        >
                          <i className="ri-history-line text-lg"></i>
                          {t('gen.tabHistory')}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="border-2 border-dashed border-gray-700 rounded-xl p-14 text-center bg-[#1e2025]" style={{ minHeight: '480px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                      <i className="ri-image-line text-6xl text-gray-700 mb-4"></i>
                      <p className="text-gray-500 text-sm font-semibold">{t('gen.resultPlaceholder')}</p>
                      <p className="text-gray-600 text-xs mt-1">由 Pollinations AI 提供支持</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* 批量生成 Tab */}
            {activeTab === 'batch' && (
              <BatchGeneration
                prompt={prompt}
                style={style}
                aspectRatio={aspectRatio}
                onComplete={() => setHistoryRefreshKey((k) => k + 1)}
              />
            )}

            {/* 图转视频 Tab */}
            {activeTab === 'i2v' && <ImageToVideo />}

            {/* 历史记录 Tab */}
            {activeTab === 'history' && (
              <ImageHistory onUsePrompt={handleUsePromptFromHistory} refreshKey={historyRefreshKey} />
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

// 根据比例计算下载尺寸
function getDlDimensions(aspectRatio: string, quality: 'hd' | '4k'): { w: number; h: number } {
  if (quality === 'hd') {
    if (aspectRatio === '16:9') return { w: 1920, h: 1080 };
    if (aspectRatio === '9:16') return { w: 1080, h: 1920 };
    return { w: 1920, h: 1920 };
  }
  // 4K
  if (aspectRatio === '16:9') return { w: 3840, h: 2160 };
  if (aspectRatio === '9:16') return { w: 2160, h: 3840 };
  return { w: 3840, h: 3840 };
}